import React from "react";
import { gql, useQuery } from "@apollo/client";
import { useForm } from "react-final-form";
import { SelectField } from "@syos-music/form";

import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";

import MouthpieceLabel from "components/mouthpiece/MouthpieceLabel";

const makeQuery = (fragment) => {
  const fragmentName = fragment?.definitions.find(
    (def) =>
      def.kind === "FragmentDefinition" &&
      def.typeCondition.name.value === "SignatureMouthpiece"
  )?.name.value;

  if (!fragmentName) throw new Error("Cannot extract fragment name");

  return gql`
    query($filters: SignatureMouthpieces_FiltersInput!) {
      signatureMouthpieces(filters: $filters, sort: "signature") {
        edges {
          node {
            id
            signature

            mouthpieceRef {
              id

              ...MouthpieceLabel_mouthpieceRef
            }

            ...${fragmentName}
          }
        }
      }
    }

    ${MouthpieceLabel.fragments.mouthpieceRef}
    ${fragment}
  `;
};

const SignatureMouthpieceSelectField = React.forwardRef(
  function SignatureMouthpieceSelectField(
    { filters, fragment, ...props },
    ref
  ) {
    const form = useForm();
    const fieldValue = form.getFieldState(props.name)?.value;

    const query = React.useMemo(() => makeQuery(fragment), [fragment]);

    const { data, loading } = useQuery(query, {
      variables: { filters },
    });

    React.useImperativeHandle(
      ref,
      () => ({
        signatureMouthpiece: data?.signatureMouthpieces.edges.find(
          ({ node }) => node.id === fieldValue
        )?.node,
      }),
      [data, fieldValue]
    );

    return (
      <SelectField
        label="Bec signature"
        {...props}
        disabled={loading || props.disabled}
        InputProps={{
          ...props.InputProps,
          startAdornment: loading ? (
            <InputAdornment position="start">
              <CircularProgress size="1em" />
            </InputAdornment>
          ) : (
            props.InputProps?.startAdornment
          ),
        }}
      >
        {loading && <MenuItem value={fieldValue ?? ""} />}

        {data?.signatureMouthpieces?.edges.map(({ node }) => (
          <MenuItem key={node.id} value={node.id}>
            <div>
              <Typography>{node.signature}</Typography>
              <Typography
                color="primary"
                sx={{ ".MuiSelect-select &": { display: "none" } }}
              >
                <small>
                  <MouthpieceLabel mouthpieceRef={node.mouthpieceRef} />
                </small>
              </Typography>
            </div>
          </MenuItem>
        ))}
      </SelectField>
    );
  }
);

SignatureMouthpieceSelectField.defaultProps = {
  filters: {},
  fragment: gql`
    fragment SignatureMouthpieceSelectField_DefaultFragment on SignatureMouthpiece {
      id
    }
  `,
};

export default SignatureMouthpieceSelectField;
